import { playerContainerFocused } from '../utils/playerEventsHelper';
import videojs from 'video.js';

let seekTimeout: any = null;
let forwardAccumulatedSeekTime = 0;
let backwardAccumulatedSeekTime = 0;

function getSeekDisplay(
  player: any,
  direction: 'forward' | 'backward'
): HTMLElement | null {
  // Look for seek display in player container, not in buttons
  const playerEl = player.el();
  return playerEl.querySelector(`.seek-time-display.${direction}`);
}

function updateSeekDisplay(
  player: any,
  time: number,
  direction: 'forward' | 'backward'
) {
  const display = getSeekDisplay(player, direction);
  // Pause player when seek is in progress.
  if (!player.paused()) {
    player.pause();
  }
  if (display) {
    display.textContent = `${direction === 'forward' ? '+' : '-'}${Math.abs(
      time
    )}s`;
    display.style.display = 'flex';
    display.classList.remove('fade-out'); // Remove fade-out class if present
    display.classList.remove('pulse-animate'); // Reset animation
    // Trigger reflow to restart animation
    void display.offsetWidth;
    display.classList.add('pulse-animate');
  }
}

function hideSeekDisplay(player: any, direction: 'forward' | 'backward') {
  const display = getSeekDisplay(player, direction);
  if (display) {
    display.classList.add('fade-out');
    setTimeout(() => {
      display.style.display = 'none';
      display.classList.remove('fade-out');
    }, 100);
  }
}

function debouncedSeek(timeSec: number, player: any) {
  clearTimeout(seekTimeout);

  if (timeSec > 0) {
    if (backwardAccumulatedSeekTime !== 0) {
      hideSeekDisplay(player, 'backward');
      backwardAccumulatedSeekTime = 0;
    }
    forwardAccumulatedSeekTime += timeSec;
    updateSeekDisplay(player, forwardAccumulatedSeekTime, 'forward');
  } else {
    // timeSec < 0
    if (forwardAccumulatedSeekTime !== 0) {
      hideSeekDisplay(player, 'forward');
      forwardAccumulatedSeekTime = 0;
    }
    backwardAccumulatedSeekTime += timeSec;
    updateSeekDisplay(player, backwardAccumulatedSeekTime, 'backward');
  }

  seekTimeout = setTimeout(() => {
    const totalSeek = forwardAccumulatedSeekTime + backwardAccumulatedSeekTime;
    if (totalSeek !== 0) {
      seek(totalSeek, player);
      if (player.paused()) {
        player.play();
      }
    }
    hideSeekDisplay(player, 'forward');
    hideSeekDisplay(player, 'backward');
    forwardAccumulatedSeekTime = 0;
    backwardAccumulatedSeekTime = 0;
  }, 700);
}

export function addSeekButtons(playerRef: any, isLiveChannel: boolean = false) {
  // seekControlsLoadedet  = true;
  const player = playerRef; // Player instance

  /* Hiding default play button */
  player.removeChild('PlayToggle');
  player.removeChild('PictureInPictureToggle');
  player.removeChild('BigPlayButton');

  /* Get control bar and remove default PlayToggle from it */
  const controlBar = player.getChild('ControlBar');
  if (controlBar) {
    controlBar.removeChild('PlayToggle');
  }

  const Button = videojs.getComponent('Button');
  const seekTime = (timeSec: number): Object => {
    return {
      clickHandler: () => debouncedSeek(timeSec, playerRef)
    };
  };
  const skipForButton = new Button(player, seekTime(10));
  const skipBackButton = new Button(player, seekTime(-10));
  skipForButton.addClass('vjs-skip-forward');
  skipForButton.controlText('Forward 10s');
  skipBackButton.addClass('vjs-skip-backward');
  skipBackButton.controlText('Backward 10s');

  const Component = videojs.getComponent('Component');

  /* Creating left panel (skip wrapper) component */
  const leftPanelComp = new Component(player);

  /* Get control bar and add left panel to it */
  if (controlBar) {
    controlBar.addChild(leftPanelComp).addClass('vjs-skip-wrapper');
  } else {
    /* Fallback: add to player if control bar is not available */
    player.addChild(leftPanelComp).addClass('vjs-skip-wrapper');
  }

  /* Adding child component to left panel */
  leftPanelComp.addChild('PlayToggle').addClass('vjs-playing');
  !isLiveChannel && leftPanelComp.addChild(skipBackButton);
  !isLiveChannel && leftPanelComp.addChild(skipForButton);

  /* Adding volume button as rightmost button */
  leftPanelComp.addChild('VolumePanel');

  // Create and append seek time display elements directly to player (centered)
  const playerEl = player.el();

  const forwardDisplay = document.createElement('div');
  forwardDisplay.className = 'seek-time-display forward';
  forwardDisplay.style.display = 'none';
  playerEl.appendChild(forwardDisplay);

  const backwardDisplay = document.createElement('div');
  backwardDisplay.className = 'seek-time-display backward';
  backwardDisplay.style.display = 'none';
  playerEl.appendChild(backwardDisplay);
}

export function seek(
  secs: number,
  videoPlayer: { hotkeys?: (event: any) => void; currentTime?: any }
) {
  if (videoPlayer) {
    window.autoSeek = true;
    let time = videoPlayer.currentTime() + secs;
    if (time < 0) time = 0;
    videoPlayer.currentTime(time);
    playerContainerFocused();
  }
}
